import { useQueryClient } from '@tanstack/vue-query'
import { log } from 'src/services/app/log.service'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { normalizeBannerListItem } from 'src/services/helpers/banners/list.helper'

/**
 * Banners multi-cache handlers.
 * Used by both the mutating tab (onSuccess) and the broadcast receiver.
 */
export function useBannersCacheHandlers() {
  const queryClient = useQueryClient()

  /**
   * Full-replaces the banner detail cache and upserts the list-item projection
   * into BANNERS_LIST (via `updateBannerInListCache`).
   *
   * @param {string} siteId
   * @param {import('src/services/helpers/banner/detail.helper').BannerDetail} banner
   */
  function setBannerInCaches(siteId, banner) {
    if (!siteId || !banner) return

    log.info('[Banners] Setting banner in caches:', siteId)
    queryClient.setQueryData([QUERY_KEYS.BANNER, siteId], banner)

    const listItem = normalizeBannerListItem(banner)
    updateBannerInListCache(queryClient, listItem)
  }

  /**
   * Evicts site-scoped detail/query caches for a deleted banner.
   * List cache is replaced from the DELETE response separately (full list).
   *
   * @param {string} siteId
   */
  function evictBannerSiteCaches(siteId) {
    if (!siteId) return

    log.info('[Banners] Evicting site-scoped caches:', siteId)

    const siteScopedQueryKeys = [
      QUERY_KEYS.BANNER,
      QUERY_KEYS.INSTALL_STATUS,
      QUERY_KEYS.SITE_SUMMARY,
      QUERY_KEYS.SCAN_STATUS,
      QUERY_KEYS.SCAN_HISTORY,
      QUERY_KEYS.SCAN_DETAIL,
    ]

    siteScopedQueryKeys.forEach((queryKey) => {
      queryClient.removeQueries({ queryKey: [queryKey, siteId] })
    })
  }

  return { setBannerInCaches, evictBannerSiteCaches }
}

/**
 * Pure function — updates (upsert) a single banner in the BANNERS_LIST cache.
 * Auto-detects add (prepend) vs update (merge) by `siteId`.
 * Only runs when the list query is mounted (no phantom cache entries).
 *
 * Exported for callers outside setup (e.g. `bannerQueryFn`) that already
 * hold a `queryClient`. Prefer `useBannersCacheHandlers` from setup.
 *
 * @param {import('@tanstack/vue-query').QueryClient} queryClient
 * @param {import('src/services/helpers/banners/list.helper').BannerListItem} bannerListItem
 */
export function updateBannerInListCache(queryClient, bannerListItem) {
  if (!bannerListItem) return

  const hasQueryData = !!queryClient.getQueryData([QUERY_KEYS.BANNERS_LIST])
  if (!hasQueryData) {
    log.info('[Banners] List query not mounted, skipping cache update')
    return
  }

  queryClient.setQueryData([QUERY_KEYS.BANNERS_LIST], (oldList) => {
    if (!oldList) return oldList

    const idx = oldList.findIndex((b) => b.siteId === bannerListItem.siteId)
    if (idx === -1) {
      log.info('[Banners] Adding new banner to list cache:', bannerListItem.siteId)
      return [bannerListItem, ...oldList]
    }

    log.info('[Banners] Updating existing banner in list cache:', bannerListItem.siteId)
    const updated = [...oldList]
    updated[idx] = { ...updated[idx], ...bannerListItem }
    return updated
  })
}

/**
 * Patches denormalized tracker counts on BANNERS_LIST and BANNER detail
 * caches from a scan summary. Called after classification (mutation + broadcast).
 *
 * @param {import('@tanstack/vue-query').QueryClient} queryClient
 * @param {string} siteId
 * @param {import('src/services/helpers/scan/status.helper').ScanSummary|null|undefined} summary
 */
export function updateBannerCountsFromScan(queryClient, siteId, summary) {
  const { totalItems, unclassifiedItems } = summary ?? {}
  if (!siteId || totalItems == null) return

  queryClient.setQueryData([QUERY_KEYS.BANNERS_LIST], (list) =>
    list?.map((b) => (b.siteId === siteId ? { ...b, totalItems, unclassifiedItems } : b))
  )
  queryClient.setQueryData([QUERY_KEYS.BANNER, siteId], (banner) =>
    banner ? { ...banner, totalItems, unclassifiedItems } : banner
  )
}
